package com.arena_web.ui.sections.home.mobile

import androidx.compose.runtime.Composable
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Div

// Ad-creative primitives for the mobile story landing — the same visual
// language as marketing/meta-ads-2026-09 (numbered chips, uppercase headlines
// with a gradient accent word, neon line-art balls, JOIN FREE pill).

private const val URBANIST = AppTypography.PRIMARY_FONT_NAME
private const val URBANIST_FB = AppTypography.PRIMARY_FONT_FALLBACK

// Numbered outline chip, e.g. "01 · EVENTS" — latin by design, not localized.
@Composable
internal fun StoryChip(text: String, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .mBorderRadius(100)
            .mPadding(v = 7, h = 16)
            .styleModifier {
                property("border", "1px solid var(--l-chip-border, rgba(126, 156, 245, 0.35))")
                property("background", "var(--l-chip-bg, rgba(9, 16, 34, 0.7))")
                property("backdrop-filter", "blur(4px)")
                property("-webkit-backdrop-filter", "blur(4px)")
            }
    ) {
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(11)
                .mLineHeight(14)
                .mLetterSpacing(2.2)
                .fontWeight(AppTypography.SemiBold)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier {
                    property("color", "var(--arena-text-2)")
                    property("white-space", "nowrap")
                }
        )
    }
}

// Uppercase headline with the **accent** span rendered as gradient text.
// The string is split on "**"; odd segments are accent. Each word is its own
// span so the row wraps naturally at any width.
@Composable
internal fun StoryHeadline(text: String, sizePx: Int) {
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("justify-content", "center")
                property("align-items", "baseline")
                property("text-align", "center")
                property("column-gap", m(8))
                property("row-gap", m(2))
                property("width", "100%")
            }
        }
    ) {
        text.split("**").forEachIndexed { i, segment ->
            val accent = i % 2 == 1
            segment.trim().takeIf { it.isNotEmpty() }?.split(" ")?.forEach { word ->
                SpanText(
                    word,
                    modifier = Modifier
                        .mFontSize(sizePx)
                        .mLineHeight(sizePx + 6)
                        .mLetterSpacing(sizePx * 0.03)
                        .fontWeight(AppTypography.Bold)
                        .fontFamily(URBANIST, URBANIST_FB)
                        .styleModifier {
                            property("text-transform", "uppercase")
                            property("white-space", "nowrap")
                            if (accent) {
                                property(
                                    "background",
                                    "linear-gradient(180deg, #9CC0FF 0%, #3477FA 100%)"
                                )
                                property("-webkit-background-clip", "text")
                                property("background-clip", "text")
                                property("color", "transparent")
                            } else {
                                property("color", "var(--l-text-strong, #F4F8FF)")
                                property("text-shadow", "var(--l-text-glow, 0 0 24px rgba(120, 160, 255, 0.25))")
                            }
                        }
                )
            }
        }
    }
}

@Composable
internal fun StorySubtitle(text: String, modifier: Modifier = Modifier) {
    SpanText(
        text,
        modifier = modifier
            .mFontSize(14)
            .mLineHeight(20)
            .mLetterSpacing(0.42)
            .fontWeight(AppTypography.Regular)
            .fontFamily(URBANIST, URBANIST_FB)
            .styleModifier {
                property("color", "var(--l-story-muted, #8FB0DF)")
                property("text-align", "center")
            }
    )
}

// Four neon line-art balls (football / basketball / tennis / volleyball),
// each glowing in its sport colour — mirrors the story ad creative.
@Composable
internal fun NeonBallsRow(modifier: Modifier = Modifier) {
    Row(
        modifier = modifier.mGap(20),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        NeonBall(StoryFootballSvg, "#7EA6FF")
        NeonBall(StoryBasketballSvg, "#FFA53B")
        NeonBall(StoryTennisSvg, "#A8E05F")
        NeonBall(StoryVolleyballSvg, "#43E3C4")
    }
}

@Composable
private fun NeonBall(svg: String, glow: String) {
    Box(
        modifier = Modifier
            .mSize(34)
            .styleModifier {
                property("background-image", "url(\"data:image/svg+xml;utf8,$svg\")")
                property("background-size", "contain")
                property("background-repeat", "no-repeat")
                property("background-position", "center")
                property("filter", "drop-shadow(0 0 6px $glow)")
                property("flex-shrink", "0")
            }
    )
}

// Solid blue CTA pill — the page's primary conversion action. Fires the
// cta_join_free analytics click before invoking [onClick].
@Composable
internal fun JoinFreePill(text: String, onClick: () -> Unit, modifier: Modifier = Modifier) {
    Row(
        modifier = modifier
            .mHeight(52)
            .mBorderRadius(100)
            .mPaddingLeft(32)
            .mPaddingRight(28)
            .mGap(8)
            .cursor(Cursor.Pointer)
            .onClick {
                com.arena_web.analytics.AnalyticsTracker.trackClick(
                    "cta_join_free", params = mapOf("layout" to "mobile"),
                )
                onClick()
            }
            .styleModifier {
                property("background", "linear-gradient(180deg, #3B82FF 0%, #1D5BFF 100%)")
                property("box-shadow", "0 8px 28px rgba(29, 91, 255, 0.45)")
                property("flex-shrink", "0")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(15)
                .mLineHeight(20)
                .mLetterSpacing(1.5)
                .fontWeight(AppTypography.Bold)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier {
                    property("color", "#FFFFFF")
                    property("text-transform", "uppercase")
                    property("white-space", "nowrap")
                }
        )
        // Crisp SVG glyph — the "↗" text char drifts off-baseline in Urbanist.
        Box(
            modifier = Modifier
                .mSize(14)
                .styleModifier {
                    property("background-image", "url(\"data:image/svg+xml;utf8,$StoryArrowUpRightSvg\")")
                    property("background-size", "contain")
                    property("background-repeat", "no-repeat")
                    property("background-position", "center")
                    property("flex-shrink", "0")
                }
        )
    }
}

// Halftone dot patch — decorative accent behind phone compositions.
@Composable
internal fun HalftoneDots(widthPx: Int, heightPx: Int, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .mWidth(widthPx)
            .mHeight(heightPx)
            .styleModifier {
                property(
                    "background-image",
                    "radial-gradient(rgba(86, 132, 255, 0.4) 1.2px, transparent 1.2px)"
                )
                property("background-size", "9px 9px")
                property(
                    "-webkit-mask-image",
                    "radial-gradient(closest-side, #000 55%, transparent)"
                )
                property(
                    "mask-image",
                    "radial-gradient(closest-side, #000 55%, transparent)"
                )
                property("pointer-events", "none")
            }
    )
}

// =============================================================================
// Neon ball artwork — URL-encoded stroke-only SVGs for data: URI use.
// =============================================================================

private val StoryArrowUpRightSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E" +
        "%3Cpath d='M7 17 L17 7 M9 7 H17 V15'/%3E" +
        "%3C/svg%3E"

private val StoryFootballSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48' fill='none' stroke='%237EA6FF' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E" +
        "%3Ccircle cx='24' cy='24' r='21'/%3E" +
        "%3Cpath d='M24 15 L32.5 21.2 L29.3 31.2 H18.7 L15.5 21.2 Z'/%3E" +
        "%3Cpath d='M24 15 V3.5 M32.5 21.2 L43.5 17.5 M29.3 31.2 L36 41 M18.7 31.2 L12 41 M15.5 21.2 L4.5 17.5'/%3E" +
        "%3C/svg%3E"

private val StoryBasketballSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48' fill='none' stroke='%23FFA53B' stroke-width='1.6' stroke-linecap='round'%3E" +
        "%3Ccircle cx='24' cy='24' r='21'/%3E" +
        "%3Cpath d='M3 24 H45 M24 3 V45'/%3E" +
        "%3Cpath d='M9 9 C18 17 18 31 9 39 M39 9 C30 17 30 31 39 39'/%3E" +
        "%3C/svg%3E"

private val StoryTennisSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48' fill='none' stroke='%23A8E05F' stroke-width='1.6' stroke-linecap='round'%3E" +
        "%3Ccircle cx='24' cy='24' r='21'/%3E" +
        "%3Cpath d='M7 8.5 C22 16 22 32 7 39.5 M41 8.5 C26 16 26 32 41 39.5'/%3E" +
        "%3C/svg%3E"

private val StoryVolleyballSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48' fill='none' stroke='%2343E3C4' stroke-width='1.6' stroke-linecap='round'%3E" +
        "%3Ccircle cx='24' cy='24' r='21'/%3E" +
        "%3Cpath d='M24 24 C24 12 16 6 7 8.5 M24 24 C35 20 42 25 43 34 M24 24 C17 33 20 42 28 44.5'/%3E" +
        "%3C/svg%3E"
